import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { isArray } from 'lodash';
import Image from 'next/image';
import Link from 'next/link';
import React, { useLayoutEffect, useState } from 'react';

import LiveAuthorCard from '@/components/Live/LiveCard/LiveAuthorCard';
import { useDragScroll } from '@/components/shared/useDragScroll';

import { IAuthorInterface } from '@/pages/books/[bookId]';

import Headings from '../landingPage/heading';
import { formatFollowerCount } from '../../ChatBox';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import {
  IAuthorDetails,
  IAuthorDetailsList,
} from '../../../utils/interfaces/landingPage';

interface BookDescriptionProps {
  data: IAuthorDetails | IAuthorDetailsList[];
  authorsData: IAuthorInterface | IAuthorInterface[] | undefined;
  isMultipleAuthors?: boolean;
  productId: string;
  productName: string;
}

const AuthorDetailsSection: React.FC<BookDescriptionProps> = ({
  data,
  authorsData,
  isMultipleAuthors = false,
  productId,
  productName,
}) => {
  const screens = useBreakpoint();
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  const { scrollRef, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const authorMap = new Map<string, IAuthorInterface>();
  if (Array.isArray(authorsData)) {
    authorsData.forEach((a) => {
      authorMap.set(a.user_name, a);
    });
  }

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const container = scrollRef?.current;
    if (!container) return;

    const checkScrollBoundaries = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftGradient(scrollLeft > 5);
      setShowRightGradient(scrollLeft + clientWidth < scrollWidth - 5);
    };

    checkScrollBoundaries();
    container.addEventListener('scroll', checkScrollBoundaries);
    window.addEventListener('resize', checkScrollBoundaries);

    return () => {
      container.removeEventListener('scroll', checkScrollBoundaries);
      window.removeEventListener('resize', checkScrollBoundaries);
    };
  }, [scrollRef?.current]);
  return (
    <div className='container mx-auto'>
      <Headings heading='MEET THE AUTHOR' />
      <ShouldRender check={!isMultipleAuthors}>
        {!isArray(data) && !isArray(authorsData) && (
          <div
            style={{
              border: '1px solid #FFFFFF24',
              background: screens.md
                ? 'radial-gradient(67.49% 179.15% at 50% 50.31%, #0F0F0F 0%, #2D2D2D 100%)'
                : '#181818',
            }}
            className='flex flex-col p-6  rounded-lg'
          >
            <div className=''>
              <div className='flex flex-row gap-x-5 md:gap-x-7 lg:gap-x-10 h-full'>
                <div className='flex flex-col items-center '>
                  <div className='flex items-center h-full md:min-w-[136px]'>
                    <ShouldRender check={data?.authorImage}>
                      <Image
                        src={data?.authorImage}
                        alt={data?.authorName as string}
                        width={136}
                        height={191}
                        className='rounded-lg'
                      />
                    </ShouldRender>
                  </div>
                </div>
                <div className='flex flex-col  '>
                  <span className='flex flex-col gap-y-2 md:gap-y-0'>
                    <div className='text-base md:text-2xl  gap-x-2 gap-y-1 flex flex-col md:flex-row  items-center font-medium'>
                      <Link
                        href={`/author/${authorsData?.user_name}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex hover:underline text-white'
                        style={{ textUnderlineOffset: '2px' }}
                      >
                        {data?.authorName}
                      </Link>

                      <ShouldRender
                        check={Number(authorsData?.follower_count) > 200}
                      >
                        <p className='bg-[#282829] text-xs md:text-sm rounded-full px-3 inline-flex py-0.5 mx-1'>
                          {formatFollowerCount(
                            Number(authorsData?.follower_count),
                          )}{' '}
                          Learners
                        </p>
                      </ShouldRender>
                    </div>{' '}
                    <span>
                      <span className='inline-flex items-center'>
                        <span className='translate-y-1 pr-1 md:pr-2'>
                          <ShouldRender check={data?.authorName}>
                            <Image
                              alt={data?.authorName || 'company_icon'}
                              src={data?.companyIcon as string}
                              width={16}
                              height={16}
                              className=''
                            />
                          </ShouldRender>
                        </span>
                      </span>
                      <span className='text-xs lg:text-sm'>
                        {' '}
                        {data?.authorDesignation}
                      </span>
                    </span>
                  </span>
                  <ShouldRender
                    check={
                      screens.md && data && data?.authorDescription?.length
                    }
                  >
                    <div className='flex flex-col mt-3 '>
                      <div className=' list-disc'>
                        <ul className='list-disc flex flex-col text-sm gap-y-1 pl-4'>
                          {data?.authorDescription?.map(
                            (contentItem, index) => (
                              <li key={`${contentItem}-${index}`}>
                                {contentItem}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  </ShouldRender>
                </div>
              </div>
            </div>
            <ShouldRender
              check={!screens.md && data && data?.authorDescription?.length}
            >
              <div className='flex flex-col mt-6'>
                <div className=' list-disc'>
                  <ul className='list-disc flex flex-col text-sm gap-y-1 pl-4'>
                    {data?.authorDescription?.map((contentItem, index) => (
                      <li key={`${contentItem}-${index}`}>{contentItem}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </ShouldRender>
          </div>
        )}
      </ShouldRender>
      <ShouldRender check={isMultipleAuthors}>
        <div className='relative w-full h-full'>
          <div
            ref={scrollRef}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            className='grid grid-flow-col gap-2 md:gap-4 no-select cursor-grab active:cursor-grabbing overflow-x-auto no-scrollbar relative '
          >
            {Array.isArray(data) &&
              data.map((author, index) => {
                const matched = authorMap.get(author?.authUser_name || '');
                return (
                  <div
                    key={`${author?.authorName}-${index}`}
                    className='flex-shrink-0 h-full '
                  >
                    <div className='h-full max-h-[340px] w-full flex'>
                      <LiveAuthorCard
                        author={{
                          name: author?.authorName || '',
                          verified: matched?.is_verified ?? false,
                          title: '',
                          learners: +(matched?.follower_count ?? 0),
                          bio: author?.authorDescription,
                          longBio: '',
                          is_followed: matched?.is_followed || false,
                          user_name: author?.authUser_name || '',
                          authorId: author?.authorId || '',
                          author_zlUid: author?.authorZlUid || '',
                          profileImage: author?.authorImage || '',
                          designation: author?.authorDesignation || '',
                          company: author?.companyIcon || '',
                        }}
                        isBookLandingPage={true}
                        productId={productId}
                        productName={productName}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
          {showLeftGradient && (
            <div
              className='pointer-events-none absolute left-0 top-0 h-full w-[5.0625rem] z-10'
              style={{
                background:
                  'linear-gradient(90deg, rgba(8, 8, 8, 0.90) 0%, rgba(8, 8, 8, 0.00) 100%)',
              }}
            />
          )}

          {showRightGradient && (
            <div
              className='pointer-events-none absolute right-0 top-0 h-full w-[5.0625rem] z-10'
              style={{
                background:
                  'linear-gradient(270deg, rgba(8, 8, 8, 0.90) 0%, rgba(8, 8, 8, 0.00) 100%)',
              }}
            />
          )}
        </div>
      </ShouldRender>
    </div>
  );
};

export default AuthorDetailsSection;
